import { HugeiconsIcon } from "@hugeicons/react";
import { Shield01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Show } from "shared/components";
import { Loader } from "shared/components/Loader";
import { Pagination } from "shared/components/Pagination";
import { SectionCard } from "shared/app/SectionCard";
import { FraudAlertRow } from "shared/app/FraudAlertRow";
import { PoweredByPropertyShield } from "shared/app/PoweredByPropertyShield";
import type { FraudAlert } from "api/propertyShield";
import { useUnitFraudAlerts } from "./controller";

const AlertsList = ({
  alerts,
  isLoading
}: {
  alerts: FraudAlert[];
  isLoading: boolean;
}) => {
  if (isLoading)
    return (
      <div className="flex justify-center py-6">
        <Loader />
      </div>
    );

  if (alerts.length === 0)
    return (
      <div className="flex flex-col items-center gap-2 py-6 text-center">
        <HugeiconsIcon
          icon={Shield01Icon}
          size={28}
          className="text-content-200"
        />
        <p className="text-sm text-content-400">
          No alerts for this unit. Property Shield will let you know if a copy
          of this listing turns up.
        </p>
      </div>
    );

  return (
    <ul className="space-y-3">
      {alerts.map((alert) => (
        <FraudAlertRow key={alert.id} alert={alert} />
      ))}
    </ul>
  );
};

export const FraudAlertsTab = ({ unitId }: { unitId?: number }) => {
  const { alerts, needsReviewCount, pageCount, page, setPage, isLoading } =
    useUnitFraudAlerts(unitId);

  return (
    <div className="mt-4 max-w-[822px] w-full">
      <SectionCard
        title="Fraudulent listing alerts"
        noPadding
        headerExtra={
          <Show if={needsReviewCount > 0}>
            <span className="text-sm font-semibold text-primary-500">
              {needsReviewCount} need{needsReviewCount === 1 ? "s" : ""} review
            </span>
          </Show>
        }
      >
        <div className="space-y-4 px-8 py-6">
          <AlertsList alerts={alerts} isLoading={isLoading} />
          <Show if={pageCount > 1}>
            <Pagination
              className="justify-center md:justify-end"
              total={pageCount}
              current={page}
              onChange={setPage}
            />
          </Show>
        </div>
        <PoweredByPropertyShield
          className="px-8 py-4 bg-alpha-20 border-t border-alpha-40 rounded-b-lg"
          description="Property Shield watches other platforms for fraudulent copies of this listing."
        />
      </SectionCard>
    </div>
  );
};
